import { useLayoutEffect, useRef, useState } from "react";
import { ChevronDown } from "react-bootstrap-icons";
import { cn, formatAmount } from "@utils";
import { useProgressiveRender } from "@hooks";
import { useAnimatedList } from "../hooks/useAnimatedList";

// How many pages the panel pulls on its own to fill a rail the first page left short. Beyond this
// the user scrolls for more: a feed whose shown rows stay sparse (the BFF filters ledger noise out
// of every window) or a very tall rail must not keep paging the API by itself.
const MAX_AUTO_PAGES = 3;

export const ActivityItem = ({ item, isLast, onSelect }) => {
  const selectable = !!item.opens && !!onSelect;
  // Money-in rows read green (payments received / requests paid both resolve to the "payment" type),
  // and carry the amount inline — "Payment received · £150".
  const isPayment = item.type === "payment";
  const title =
    isPayment && item.amount != null ? `${item.title} · ${formatAmount(item.amount)}` : item.title;
  // The sub-line is customer · when, with the actor (when the row is someone else's work) appended
  // as plain text rather than an avatar.
  const meta = item.actorName ? `${item.meta} by ${item.actorName}` : item.meta;

  const content = (
    <div className={cn("flex min-w-0 flex-col gap-0.5", !isLast && "pb-4")}>
      <p className={cn("text-sm font-semibold", isPayment ? "text-green-600" : "text-zinc-800")}>
        {title}
      </p>
      <p className="min-w-0 truncate text-xs text-zinc-500">{meta}</p>
    </div>
  );

  // The grid-collapse wrapper (grid-template-rows 0fr↔1fr) is only used while a row is actively
  // entering or leaving. At rest it's a plain block: Safari mis-sizes a lone `1fr` grid row, so
  // leaving it on permanently made resting rows render too tall before settling.
  const animating = item.leaving || item.entering === true;

  return (
    <li
      className={cn(
        animating && "grid grid-rows-[1fr]",
        item.leaving
          ? "animate-activityOut"
          : item.entering === true && "animate-activityIn",
      )}
    >
      {/* While animating, the grid row slides 0fr↔1fr so neighbours ease instead of snapping;
          min-h-0 + overflow-hidden let the row actually collapse. Genuinely-new rows also flash a
          soft brand tint that fades, so fresh activity catches the eye. */}
      <div
        className={cn(
          "rounded-lg",
          animating && "min-h-0 overflow-hidden",
          item.entering === true && "animate-activityHighlight",
        )}
      >
        {selectable ? (
          <div
            role="button"
            tabIndex={0}
            onClick={() => onSelect(item)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(item);
              }
            }}
            className="-m-1 rounded-lg p-1 transition-colors hover:bg-zinc-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
          >
            {content}
          </div>
        ) : (
          content
        )}
      </div>
    </li>
  );
};

// The fewest rows the block keeps visible: the floor an expanded schedule above it may squeeze it
// to — below this the schedule stops growing and scrolls instead.
export const MIN_VISIBLE_ROWS = 3;

// Measures how tall the whole block must stay to show its first MIN_VISIBLE_ROWS rows (or all of
// them when there are fewer): the block's fixed chrome (header, card padding) plus the bottom edge
// of the n-th row. Also hands back that rows-only height, which caps the list while collapsed. Measured rather than assumed so wording, badges or the enter animation never
// break the rule; re-measured whenever the list's size changes. The minimum has to sit on the
// block itself — a flex item's automatic minimum is its full content, so without an explicit one
// the block would never yield space to the schedule above it.
const useMinBlockHeight = (rootRef, listRef, count) => {
  const [heights, setHeights] = useState({
    minHeight: undefined,
    rowsHeight: undefined,
  });

  useLayoutEffect(() => {
    const root = rootRef.current;
    const list = listRef.current;
    if (!root || !list || typeof ResizeObserver === "undefined")
      return undefined;

    const measure = () => {
      const rows = list.querySelectorAll(":scope > ul > li");
      const n = Math.min(rows.length, MIN_VISIBLE_ROWS);
      if (n === 0) {
        setHeights({ minHeight: undefined, rowsHeight: undefined });
        return;
      }
      // Rows are positioned relative to the list (it's the offsetParent), so the bottom edge of the
      // n-th row is exactly the list height needed to show n rows. Everything in the block that
      // isn't the list is constant chrome.
      const last = rows[n - 1];
      const rowsHeight = Math.ceil(last.offsetTop + last.offsetHeight);
      const chrome =
        root.getBoundingClientRect().height -
        list.getBoundingClientRect().height;
      setHeights({ minHeight: Math.ceil(chrome) + rowsHeight, rowsHeight });
    };

    measure();
    // Watch the rows themselves, not just the scroll container: when the block is squeezed to its
    // minimum the container's height is fixed and never resizes, but the rows still grow as their
    // enter animation plays.
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    const rows = list.querySelector(":scope > ul");
    if (rows) observer.observe(rows);
    return () => observer.disconnect();
  }, [rootRef, listRef, count]);

  return heights;
};

const ActivityPanel = ({
  items = [],
  onSelect,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}) => {
  const rendered = useAnimatedList(items);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const { minHeight } = useMinBlockHeight(rootRef, listRef, rendered.length);
  // The scroll-hint chevron shows only until the user starts scrolling; the whole bottom fade lifts
  // once there's nothing more below (scrolled to the end, or the list never overflowed).
  const [scrolled, setScrolled] = useState(false);
  const [atBottom, setAtBottom] = useState(true);

  const trackScroll = (el) => {
    setScrolled(el.scrollTop > 0);
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight <= 4);
  };

  // Re-evaluate the fade when the list's contents change (rows loaded, paged in): a list that now
  // fits shows no fade, one that just overflowed shows it again.
  useLayoutEffect(() => {
    if (listRef.current) trackScroll(listRef.current);
  }, [rendered.length]);

  // Prime the pump. The load-more sentinel only fires once the list overflows and can be scrolled,
  // but a sparse first page (the BFF filters a wide window down to a few "shown" rows) can leave
  // the list shorter than the rail — so nothing ever asks for more. Pull pages until the list
  // overflows, the feed ends, or the auto-page budget is spent; the budget starts over only when
  // the list is emptied (a fresh feed), not on every refetch.
  const autoPagesRef = useRef(0);
  useLayoutEffect(() => {
    if (rendered.length === 0) autoPagesRef.current = 0;
    const list = listRef.current;
    if (!list || !hasMore || isLoadingMore || !onLoadMore) return;
    if (autoPagesRef.current >= MAX_AUTO_PAGES) return;
    const overflows = list.scrollHeight > list.clientHeight + 4;
    if (list.clientHeight > 0 && !overflows) {
      autoPagesRef.current += 1;
      onLoadMore();
    }
  }, [rendered.length, hasMore, isLoadingMore, onLoadMore]);

  // Infinite scroll within the block's own scroll container: a sentinel after the last row asks
  // for the next page as it nears the visible area. Server-paged, so no client-side batching.
  const { sentinelRef } = useProgressiveRender(rendered.length, {
    enabled: false,
    rootRef: listRef,
    rootMargin: "120px",
    onLoadMore: isLoadingMore ? undefined : onLoadMore,
    hasMoreData: hasMore,
  });

  return (
    // Sized to its content, up to whatever the rail has left under the schedule, then it shrinks
    // and scrolls inside itself. The large shrink factor makes the block give up space before the
    // schedule does, and the measured minimum stops the schedule squeezing it below its minimum
    // rows — or below its content when there are fewer.
    <div
      ref={rootRef}
      className="flex min-h-0 flex-col gap-4 [flex:1_1_auto]"
      style={{ minHeight }}
    >
      <div className="flex shrink-0 flex-col gap-1">
        <h4 className="text-sm font-semibold text-zinc-800">Activity</h4>
        <p className="text-xs text-zinc-500">Live across all accounts</p>
      </div>

      {/* Padding lives inside the scroll area (not on the card) so the edge fade dissolves content
          into the card's top/bottom rather than leaving a hard gap before the border. */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white">
        {rendered.length === 0 ? (
          <p className="p-4 text-xs text-zinc-400">Waiting for activity…</p>
        ) : (
          <>
            {/* Extra bottom padding clears the floating Ontik button: the card reaches the rail's
                bottom edge, but its last rows scroll to a stop above the button rather than under it. */}
            <div
              ref={listRef}
              onScroll={(e) => trackScroll(e.currentTarget)}
              className="relative min-h-0 flex-1 overflow-y-auto px-4 pb-16 pt-4"
            >
              <ul className="flex flex-col">
                {rendered.map((item, i) => (
                  <ActivityItem
                    key={item.id}
                    item={item}
                    isLast={i === rendered.length - 1}
                    onSelect={onSelect}
                  />
                ))}
              </ul>
              {hasMore && (
                <div ref={sentinelRef} aria-hidden="true" className="h-px" />
              )}
              {isLoadingMore && (
                <p className="pt-3 text-xs text-zinc-400">Loading more…</p>
              )}
            </div>
            <div
              className={cn(
                "pointer-events-none absolute inset-x-0 bottom-0 flex h-10 items-end justify-center bg-gradient-to-t from-white to-transparent transition-opacity",
                atBottom ? "opacity-0" : "opacity-100",
              )}
            >
              <ChevronDown
                aria-hidden="true"
                className={cn("mb-1 size-3.5 text-zinc-400 transition-opacity", scrolled ? "opacity-0" : "opacity-100")}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityPanel;
